import React, { useEffect } from 'react';
import { CheckCircle, Target, Heart } from 'lucide-react';
import about1 from '../assets/aboutpage_images/about1.jpg';
import about2 from '../assets/aboutpage_images/about2.jpg';
import about3 from '../assets/aboutpage_images/about3.jpg';

const About = () => {
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="font-sans text-[#424242] bg-white min-h-screen">
      {/* Header Section */}
      <div className="bg-indigo-700 py-20 px-5 text-center text-white relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-indigo-900/50 to-violet-900/50"></div>
        <div className="absolute top-10 right-10 text-white/5 animate-pulse">
          <Heart size={200} fill="currentColor" />
        </div>
        <div className="relative z-10 max-w-[800px] mx-auto">
          <h1 className="text-4xl md:text-6xl font-bold mb-4 ">About Us</h1>
          <p className="text-xl text-indigo-100 font-light">
            Dedicated to empowering children and families through compassionate care and expert therapy.
          </p>
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-5 py-20">

        {/* Story Section */}
        <div className="flex flex-col md:flex-row gap-12 items-center mb-24">
          <div className="flex-1">
            <h2 className="text-indigo-600 text-sm font-bold uppercase tracking-[0.2em] mb-4">Our Story</h2>
            <h3 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6 leading-tight">
              Where compassion meets expertise.
            </h3>
            <div className="space-y-6 text-gray-600 leading-relaxed text-lg">
              <p>
                Kids Work Rehab Center was founded by experienced medical and industry professionals with a strong commitment to helping children with special needs grow, learn, and live confidently.
              </p>
              <p>
                We focus on empowering children and their families through early support, education, therapy, counseling, and skill development. Our goal is to create a safe, caring, and encouraging environment where every child gets the attention and care they deserve.
              </p>
            </div>
          </div>
          <div className="flex-1 relative">
            <div className="absolute -inset-4 bg-indigo-100 rounded-full blur-2xl"></div>
            <img src={about1} alt="Therapy Session" className="relative rounded-[2rem] shadow-2xl w-full h-auto object-cover transform md:rotate-2 border-4 border-white" />
          </div>
        </div>

        {/* Mission Section */}
        <div className="bg-[#eef2ff] rounded-[3rem] p-10 md:p-16 text-center relative overflow-hidden mb-24">
          <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-indigo-600 to-violet-600"></div>
          <div className="inline-block p-4 bg-white rounded-full shadow-lg text-indigo-700 mb-6">
            <Target size={40} />
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-gray-800 mb-6">Our Mission</h2>
          <p className="text-2xl text-gray-600 italic max-w-[800px] mx-auto leading-relaxed">
            "To help every child learn, grow, and thrive—at their own pace."
          </p>
        </div>

        {/* Team Section */}
        <div className="mb-24">
          <div className="text-center mb-16">
            <h2 className="text-indigo-600 text-sm font-bold uppercase tracking-[0.2em] mb-4">Our Team</h2>
            <h3 className="text-3xl md:text-4xl font-bold text-gray-800">Multidisciplinary Excellence</h3>
            <p className="mt-4 text-gray-600 max-w-[600px] mx-auto">
              Our rehabilitation center is supported by a highly trained and dedicated team of specialists.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              'Occupational Therapists',
              'Speech Therapists',
              'Special Education Teachers',
              'Early Intervention Specialists',
              'Behavioral Therapists',
              'Child Psychologists'
            ].map((role, index) => (
              <div key={index} className="flex items-center gap-4 bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-all hover:border-indigo-100 group">
                <div className="bg-indigo-50 p-3 rounded-full text-indigo-700 group-hover:bg-indigo-700 group-hover:text-white transition-colors">
                  <CheckCircle size={20} />
                </div>
                <span className="font-semibold text-gray-700">{role}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Values/Philosophy Section */}
        <div className="flex flex-col md:flex-row-reverse gap-12 items-center">
          <div className="flex-1">
            <h2 className="text-indigo-600 text-sm font-bold uppercase tracking-[0.2em] mb-4">Our Approach</h2>
            <h3 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6 leading-tight">
              Holistic & Child-Centered
            </h3>
            <div className="space-y-6 text-gray-600 leading-relaxed text-lg">
              <p>
                Each child is unique, and so is our approach. We design personalized plans that support a child’s physical, emotional, social, and learning development.
              </p>
              <p>
                We work closely with parents and caregivers, believing that family involvement is key to long-term progress and success. Together, we create a strong support system.
              </p>
            </div>
          </div>
          <div className="flex-1 grid grid-cols-2 gap-4">
            <img src={about2} alt="Activity" className="rounded-2xl shadow-lg w-full h-[250px] object-cover mt-8" />
            <img src={about3} alt="Learning" className="rounded-2xl shadow-lg w-full h-[250px] object-cover" />
          </div>
        </div>

      </div>
    </div>
  );
};

export default About;
